import { useState, useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  algoliaQueryAction,
  getPriceInventoryGeneral,
} from 'redux/actions/priceProductsAction'
import { fetchPriceInventory } from 'Utils/functions'
import { NO_INSTALLATION_OPTION } from 'Utils/constants'
import { t } from 'i18next'

export const useInstallationServices = (
  services,
  { syncWithCart = true } = {}
) => {
  const dispatch = useDispatch()

  const cart = useSelector(s => s.cart.cart)
  const companyId = useSelector(s => s.company.CompanyId)
  const warehouse = useSelector(s => s.company.WarehouseId)
  const companyCode = useSelector(s => s.company.CompanyCode)

  const [servicesDataMap, setServicesDataMap] = useState({})
  const [selectedServices, setSelectedServices] = useState([])

  // -- Helpers ----------------------------------
  const getPrices = async payload => {
    try {
      const res = await dispatch(getPriceInventoryGeneral(payload))
      return res.data?.Data
    } catch {
      return []
    }
  }

  const fetchAllServiceData = async () => {
    try {
      const systemCodes = services.map(s => s.systemCode).filter(Boolean)
      const responses = await Promise.all(
        systemCodes.map(code => dispatch(algoliaQueryAction({ query: code })))
      )

      const hits = responses.flatMap(res => res?.data?.hits || [])

      const inventoryData = await fetchPriceInventory({
        hits,
        warehouse,
        companyCode,
        companyId,
        getPrices,
        t,
      })

      const map = {}
      inventoryData.forEach(d => {
        if (d?.ProductId) map[d.ProductId] = d
      })

      setServicesDataMap(map)
    } catch (e) {
      console.error('Error loading services:', e)
    }
  }

  // -- Fetch data on mount ----------------------------------
  useEffect(() => {
    if (services.length > 0) fetchAllServiceData()
  }, [services])

  // Detect selected based on cart
  useEffect(() => {
    // It only syncs with the cart if syncWithCart === true
    if (!syncWithCart) return

    const validIds = new Set(services.map(s => s.productId))
    const updated = cart
      .filter(item => validIds.has(item.ProductId))
      .map(item => ({
        productId: item.ProductId,
        name: item.ProductName,
        ...item,
      }))

    setSelectedServices(updated)
  }, [cart, services])

  // -- Memo: build options ---------------------
  const servicesData = useMemo(() => {
    const valid = services
      .map(s => {
        const data = servicesDataMap[s.productId]
        if (!data?.FinalPrice) return null
        return {
          ...data,
          name: s.name,
          systemCode: s.systemCode,
          productId: s.productId,
        }
      })
      .filter(Boolean)

    return [NO_INSTALLATION_OPTION, ...valid]
  }, [services, servicesDataMap])

  const effectiveSelected =
    selectedServices.length === 0 ? [NO_INSTALLATION_OPTION] : selectedServices

  // Local selection handler (parent decides behavior)
  const selectService = useCallback(selectedItem => {
    setSelectedServices(prev =>
      prev.some(s => s.productId === selectedItem.productId)
        ? prev
        : [...prev, selectedItem]
    )
  }, [])

  const resetSelection = useCallback(() => {
    setSelectedServices([])
  }, [])

  const removeService = useCallback(productId => {
    setSelectedServices(prev => prev.filter(s => s.productId !== productId))
  }, [])

  return {
    servicesData,
    selectedServices: effectiveSelected,
    rawSelected: selectedServices,
    selectService,
    removeService,
    resetSelection,
    cart,
  }
}
